import { useEffect, useRef } from 'react';

import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import type { Slice } from '@atlaskit/editor-prosemirror/model';

import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import { extractLinksFromSlice, getDetectedLinksFromUrls } from './link-detector.tsx';
import { useIntegrationLinkSpotlight } from './use-integration-link-spotlight.tsx';

/**
 * Minimal type for paste plugin state (from editorAPI paste plugin).
 */
type PasteState = {
	lastContentPasted?: { pastedSlice: Slice } | null;
};

/**
 * Encapsulates integration link spotlight logic for the full-page editor:
 * - Sets baseline via requestDocument when editor is ready (supported API).
 * - On paste, extracts links from pasted slice only (efficient, no full doc traversal).
 *
 * Uses editorAPI.core.actions.requestDocument (supported API) for baseline and
 * extractLinksFromSlice for efficient paste-time link detection.
 */
export const useIntegrationLinkSpotlightForFullPageEditor = (
	editorAPI: Parameters<typeof usePluginStateEffect>[0] | undefined,
) => {
	const {
		detectedLinks,
		newLinkTypes,
		newLinks,
		shouldShowSpotlight,
		closeSpotlight,
		setInitialContent,
		handlePastedLinks,
	} = useIntegrationLinkSpotlight();

	const hasSetBaselineRef = useRef(false);
	const editorAPIRef = useRef(editorAPI);

	// Reset baseline flag when editorAPI changes
	useEffect(() => {
		if (editorAPIRef.current !== editorAPI) {
			editorAPIRef.current = editorAPI;
			hasSetBaselineRef.current = false;
		}
	}, [editorAPI]);

	// Baseline: when editor is ready, capture initial document so we can compute delta later.
	useEffect(() => {
		if (
			!editorAPI?.core?.actions?.requestDocument ||
			!fg('link-paste-spotlight-experiment-gate') ||
			!expValEquals('link-paste-spotlight-experiment', 'cohort', 'variation')
		) {
			return;
		}
		editorAPI.core.actions.requestDocument(
			(doc) => {
				if (hasSetBaselineRef.current) {
					return;
				}
				hasSetBaselineRef.current = true;
				setInitialContent(doc as any);
			},
			{ alwaysFire: true },
		);
	}, [editorAPI, setInitialContent]);

	// On paste: extract links from pasted slice only (efficient - no full doc traversal).
	usePluginStateEffect(editorAPI, ['paste'], ({ pasteState }: { pasteState?: PasteState }) => {
		const lastPasted = pasteState?.lastContentPasted;
		if (
			!lastPasted?.pastedSlice ||
			!fg('link-paste-spotlight-experiment-gate') ||
			!expValEquals('link-paste-spotlight-experiment', 'cohort', 'variation')
		) {
			return;
		}

		// Extract links from ONLY the pasted slice (not entire document)
		const pastedUrls = extractLinksFromSlice(lastPasted.pastedSlice);
		const pastedLinks = getDetectedLinksFromUrls(pastedUrls);

		// Use handlePastedLinks to process just the pasted links
		handlePastedLinks(pastedLinks);
	});

	return {
		detectedLinks,
		newLinkTypes,
		newLinks,
		shouldShowSpotlight,
		closeSpotlight,
	};
};
